// ── Design decisions ──
// Búsqueda de catálogo DGA por fila, pensada para el ExtractionDrawer: el
// operador ya tiene una descripción (la que leyó la IA) y lo que necesita es
// confirmar a qué fila del catálogo corresponde, sin abrir nada.
//
// · Dos tiempos: interpretarDescripcion (sincrónica, sin red) pinta los chips
//   de interpretación al instante; buscarCatalogo (350 ms después de que la
//   descripción se asienta) trae el top-3 y sus interpretaciones, que
//   reemplazan a las sincrónicas cuando llegan (son las mismas, pero manda la
//   respuesta real).
// · NUNCA se elige sola: aunque haya una sola coincidencia, el gestor tiene que
//   tocar la fila. Ese clic llama a onElegir(row) y el drawer decide qué campos
//   llenar (customs_catalog_id + precio desde cip_usd, sobreescribible).
// · Ligado (customs_catalog_id presente) ⇒ chip teal "Catálogo ✓ <desc>" +
//   "Desligar"; no se busca mientras está ligado.
// · La última respuesta gana (contador de solicitud); los errores no son
//   alertas: una búsqueda fallida no debe robar el foco de la extracción.
// · onHoverCandidato(row | null) avisa al padre qué candidato está bajo el
//   mouse para que previsualice el CIP donde le convenga; acá no se guarda
//   nada de ese hover.
// · buscarCatalogo se importa del módulo para que los tests lo simulen con
//   jest.mock y nunca toquen Supabase.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { buscarCatalogo, interpretarDescripcion } from '../../../utils/catalogSearch';
import InterpretacionChips from '../../shared/InterpretacionChips';
import { fmtUsd } from './labels';

export const DEBOUNCE_MS = 350;
const MAX_FILAS = 3;

// El catálogo trae descripciones repetidas (10 duplicados exactos): dos filas
// idénticas en el picker no aportan nada, así que queda la mejor rankeada.
const dedupPorDescripcion = (filas) => {
  const vistas = new Set();
  return filas.filter((f) => {
    const d = String(f?.descripcion || '');
    if (vistas.has(d)) return false;
    vistas.add(d);
    return true;
  });
};

const CatalogSugerencias = ({
  descripcion,
  ligado = null,
  onElegir,
  onDesligar,
  onHoverCandidato,
  disabled = false,
}) => {
  const [estado, setEstado] = useState('idle'); // idle | buscando | ok | error
  const [resultados, setResultados] = useState([]);
  const [interpretacionesAsync, setInterpretacionesAsync] = useState(null);
  const solicitud = useRef(0);

  const texto = String(descripcion || '').trim();

  const interpretacionesSync = useMemo(() => interpretarDescripcion(texto).interpretaciones, [texto]);

  useEffect(() => {
    // Un cambio de descripción invalida lo que vino de la red.
    setInterpretacionesAsync(null);
    if (ligado || disabled || !texto) {
      setResultados([]);
      setEstado('idle');
      return undefined;
    }
    const id = ++solicitud.current;
    setEstado('buscando');
    const t = setTimeout(async () => {
      try {
        const res = await buscarCatalogo(texto, { limit: MAX_FILAS });
        if (id !== solicitud.current) return;
        setResultados(
          dedupPorDescripcion(Array.isArray(res?.resultados) ? res.resultados : []).slice(0, MAX_FILAS)
        );
        setInterpretacionesAsync(Array.isArray(res?.interpretaciones) ? res.interpretaciones : []);
        setEstado(res?.error ? 'error' : 'ok');
      } catch (err) {
        if (id !== solicitud.current) return;
        setResultados([]);
        setEstado('error');
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [texto, ligado, disabled]);

  const interpretaciones = interpretacionesAsync ?? interpretacionesSync;

  if (ligado) {
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <span className="amb-chip amb-chip--cat" title="Ligado al catálogo DGA">
          <Check size={10} aria-hidden="true" /> Catálogo ✓
          {ligado.descripcion ? <span className="truncate font-normal">{ligado.descripcion}</span> : null}
        </span>
        {!disabled && (
          <button
            type="button"
            onClick={() => onDesligar && onDesligar()}
            aria-label="Desligar del catálogo"
            title="Desligar del catálogo: vuelve a valores manuales"
            className="amb-link"
          >
            Desligar
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      <InterpretacionChips interpretaciones={interpretaciones} ambiance />
      {texto && estado === 'buscando' && (
        <p className="amb-status">
          <Loader2 size={10} className="animate-spin" aria-hidden="true" /> Buscando en el catálogo…
        </p>
      )}
      {texto && estado === 'error' && (
        <p className="amb-status amb-status--error">No se pudo consultar el catálogo.</p>
      )}
      {texto && estado === 'ok' && resultados.length === 0 && (
        <p className="amb-status">Sin coincidencias en el catálogo — queda manual.</p>
      )}
      {resultados.length > 0 && (
        <ul aria-label="Sugerencias del catálogo" className="flex flex-col gap-1">
          {resultados.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onElegir && onElegir(row)}
                onMouseEnter={() => onHoverCandidato && onHoverCandidato(row)}
                onMouseLeave={() => onHoverCandidato && onHoverCandidato(null)}
                title={`Usar ${row.descripcion} (CIP $${fmtUsd(row.cip_usd)})`}
                aria-label={`Usar ${row.descripcion} (CIP $${fmtUsd(row.cip_usd)})`}
                className="amb-suggest"
              >
                <span className="truncate">{row.descripcion}</span>
                <span className="amb-suggest-cip">CIP ${fmtUsd(row.cip_usd)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CatalogSugerencias;
